import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCalibrationRuntimeRead } from '@features/calibration/hooks/useCalibrationRuntime/useCalibrationRuntimeRead';
import { useCalibrationRuntimeMutations } from '@features/calibration/hooks/useCalibrationRuntime/useCalibrationRuntimeMutations';
import { useGroupedDatasheet } from '@features/calibration/hooks/useGroupedDatasheet';
import type { SyncStatus } from '@features/calibration/hooks/useCalibrationRuntime/types';
import { useRunnerLiveFill } from './hooks/useRunnerLiveFill';
import RunnerSection from './components/RunnerSection';
import type {
  CalibrationRunnerProps,
  MeasurementSide,
  RunnerLiveState,
  RunnerSaveStatus,
  RunnerVariant
} from './types';

const CalibrationRunner: React.FC<CalibrationRunnerProps> = ({
  dcRuntimeId,
  live,
  readOnly = false
}) => {
  const { t } = useTranslation('calibration');

  const isMobile = useMediaQuery('(max-width: 599px)');
  const isTablet = useMediaQuery('(min-width: 600px) and (max-width: 1450px)');
  const variant: RunnerVariant = isMobile
    ? 'mobile'
    : isTablet
      ? 'tablet'
      : 'desktop';

  const {
    calibrationExists,
    calibrationRuntime,
    calibrationData,
    isCheckingCalibration,
    error
  } = useCalibrationRuntimeRead({ dcRuntimeId });

  const { overrideMeasurement } = useCalibrationRuntimeMutations({
    gagesGlobalId: calibrationRuntime?.gagesGlobalId ?? '',
    woDetailGlobalId: '',
    moduleType: 'Voltmeter'
  });

  const liveFill = useRunnerLiveFill(live);

  const mergedPoints = useMemo(() => {
    const points = calibrationData?.tests?.basicReading?.testPoints;
    if (!points || Object.keys(liveFill.liveReadings).length === 0) {
      return points;
    }

    return points.map(point => {
      const reading = liveFill.liveReadings[point.id];
      if (!reading) return point;

      const merged = { ...point };
      if (reading.asFound && !point.asFoundOverridden) {
        merged.asFound = reading.asFound.value;
        merged.asFoundUnit = reading.asFound.unit ?? merged.asFoundUnit;
        merged.asFoundImageId =
          reading.asFound.imageId ?? merged.asFoundImageId;
      }
      if (reading.asLeft && !point.asLeftOverridden) {
        merged.asLeft = reading.asLeft.value;
        merged.asLeftUnit = reading.asLeft.unit ?? merged.asLeftUnit;
        merged.asLeftImageId = reading.asLeft.imageId ?? merged.asLeftImageId;
      }
      return merged;
    });
  }, [calibrationData?.tests?.basicReading?.testPoints, liveFill.liveReadings]);

  const groups = useGroupedDatasheet(mergedPoints);

  const liveState = useMemo<RunnerLiveState | undefined>(() => {
    if (!live) return undefined;

    return {
      currentPointId: liveFill.currentPointId,
      landedPointId: liveFill.landedPointId,
      landedSide: liveFill.landedSide,
      landedAt: liveFill.landedAt
    };
  }, [live, liveFill]);

  const robotStatus = useMemo(() => {
    if (!live || liveFill.runStatus !== 'RUNNING') return undefined;

    const currentPoint = mergedPoints?.find(
      p => p.id === liveFill.currentPointId
    );

    return (
      <Typography
        variant="body2"
        sx={{ color: 'warning.main', fontWeight: 600 }}
      >
        ●{' '}
        {currentPoint
          ? t('runner.robotReading', {
              point: currentPoint.label ?? currentPoint.id
            })
          : t('runner.robotRunning', {
              current: liveFill.currentItemIndex,
              total: liveFill.totalItems
            })}
      </Typography>
    );
  }, [live, liveFill, mergedPoints, t]);

  const [expandedByGroup, setExpandedByGroup] = useState<
    Record<string, boolean>
  >({});

  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('synced');

  const handleExpandedChange = useCallback(
    (groupKey: string, expanded: boolean) => {
      setExpandedByGroup(prev => ({ ...prev, [groupKey]: expanded }));
    },
    []
  );

  const handleCommitMeasurement = useCallback(
    async (pointId: string, side: MeasurementSide, value: number | null) => {
      if (!calibrationRuntime?.id || value === null) return;

      setIsSaving(true);
      setSyncStatus('pending');
      setSaveError(null);

      try {
        await overrideMeasurement(calibrationRuntime.id, pointId, side, value);
        setSyncStatus('synced');
        setLastSaved(new Date());
      } catch (overrideError) {
        setSyncStatus('error');
        setSaveError(
          overrideError instanceof Error
            ? overrideError.message
            : 'Failed to save measurement override'
        );
      } finally {
        setIsSaving(false);
      }
    },
    [calibrationRuntime?.id, overrideMeasurement]
  );

  const saveStatus: RunnerSaveStatus = {
    isSaving,
    lastSaved,
    saveError,
    syncStatus
  };

  if (isCheckingCalibration) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (!calibrationExists || groups.length === 0) {
    return <Alert severity="info">{t('runner.noDatasheet')}</Alert>;
  }

  return (
    <Box>
      {groups.map((group, index) => (
        <RunnerSection
          key={group.groupKey}
          group={group}
          expanded={
            expandedByGroup[group.groupKey] ??
            (index === 0 || group.overallStatus !== 'not-started')
          }
          onExpandedChange={expanded =>
            handleExpandedChange(group.groupKey, expanded)
          }
          readOnly={readOnly}
          saveStatus={readOnly ? undefined : saveStatus}
          onCommitMeasurement={readOnly ? undefined : handleCommitMeasurement}
          liveState={liveState}
          robotStatus={robotStatus}
          runId={calibrationData?.config?.runId ?? live?.runId}
          variant={variant}
        />
      ))}
    </Box>
  );
};

export default CalibrationRunner;
